/* istanbul ignore file */
import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LINE_HEIGHT, SPACES } from 'common/constants/sizes';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { useValidate } from 'components/hooks/useValidate';
import _ from 'lodash';
import {
  getAzureBoardsOrCHReauthSensorBody,
  getAzureBoardsNewSensorBody,
  extractOrgNameFromAzureBoardsSensor,
  validateAzureBoardsToken
} from '../../../services/project-management.service';
import { TRACKING, PROVIDERS_MAP } from 'common/constants/constants';
import {
  createSensorAuthorization,
  updateSensorAuthorization
} from 'state/callers';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { SENSOR_STATES } from '@linearb/linjs-common';
import * as TrackingService from 'common/services/tracking.service';
import { cn, LBAzureBoardsIcon, LBButton, LBInput } from '@linearb/buzz';

const AZURE_BOARDS = PROVIDERS_MAP.azure_boards;

const ConnectView = () => {
  const reauthSensor = useSelector(pmIntegrationSelector);
  const isReAuth = reauthSensor && reauthSensor.state !== SENSOR_STATES.ok;

  const dispatch = useDispatch();
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const [token, setToken] = useState('');
  const [orgName, setOrgName] = useState<string>(
    isReAuth
      ? (extractOrgNameFromAzureBoardsSensor(reauthSensor) as string)
      : ''
  );
  const tokenValidation = useCallback(
    async token => validateAzureBoardsToken(token, orgName),
    [token, orgName]
  );

  const actionWord = isReAuth ? 'Reauthorize' : 'Connect';

  const { isValid, error, isLoading, validationResponse } = useValidate({
    validateFn: tokenValidation,
    text: token,
    debounce: 1000
  });

  const onCreate = async () => {
    const body = getAzureBoardsNewSensorBody({
      token,
      orgName,
      username: _.get(validationResponse, 'username', null)
    });
    dispatch(
      createSensorAuthorization(body, { withPostActions: !isTeamLicense })
    );
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      // @ts-ignore
      page_tab: TRACKING.PAGE_TABS.AZURE_BOARDS,
      click_type: TRACKING.CLICK.SETUP_PM_CONNECT_COMPLETED
    });
  };

  const onReauth = async () => {
    const body = getAzureBoardsOrCHReauthSensorBody({ newToken: token });
    dispatch(updateSensorAuthorization(reauthSensor.id, body));
  };

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      // @ts-ignore
      page_tab: TRACKING.PAGE_TABS.AZURE_BOARDS,
      click_type: TRACKING.CLICK.SETUP_PM_CONNECT_START
    });
  }, []);

  return (
    <>
      <Flex items='center' pb='medium'>
        <LBAzureBoardsIcon className='size-5' />
        <Text
          size='large'
          weight='500'
          pl={SPACES.small}
          textTransform='uppercase'
        >
          {actionWord} {AZURE_BOARDS.name}
        </Text>
      </Flex>
      <Text
        size='medium'
        color='shuttleGray'
        lineHeight={LINE_HEIGHT.medium}
        pb={SPACES.medium}
      >
        Connect your project management tool to LinearB to track your work and
        correlate
        <br />
        your projects with what’s being done in Git
      </Text>

      <LBInput
        data-cy='chTokenInput'
        type='text'
        className='w-96'
        placeholder='Enter organization name'
        onChange={event => setOrgName(event.target.value)}
        value={orgName}
        disabled={isReAuth}
      />

      <br />

      <LBInput
        data-cy='azure-boards-pat'
        type='text'
        name='azure-boards-pat'
        placeholder='Enter personal access token (PAT)'
        onChange={event => setToken(event.target.value)}
        value={token}
        className={cn('w-96', {
          'border-destructive': !isLoading && error
        })}
        disabled={orgName.length < 2 || isLoading}
      />

      <Flex items='center' pt='normal'>
        <LBButton
          disabled={!isValid}
          data-cy='azure-connect-with-token'
          onClick={isReAuth ? onReauth : onCreate}
        >
          {actionWord}
        </LBButton>
        {error ? (
          <Text pl={SPACES['small']} size='medium' color='torchRed'>
            The token is not valid
          </Text>
        ) : null}
      </Flex>

      <Padding t='medium' />
    </>
  );
};

export default ConnectView;
